import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import config from '../../../../../config';

export const useHealthMetrics = (userInfo) => {
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [healthMetrics, setHealthMetrics] = useState(null);
  const [timeRange, setTimeRange] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    return {
      fromDate: `${today}T00:00:00`,
      toDate: `${today}T23:59:59`
    };
  });
  const [timeRangeType, setTimeRangeType] = useState('today');

  const calculateDateRange = useCallback((type, customFrom = '', customTo = '') => {
    const now = new Date();
    const start = new Date();
    const end = new Date();
    
    switch(type) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'week':
        start.setDate(start.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'month':
        start.setDate(start.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'custom':
        if (customFrom && customTo) {
          const customStart = new Date(customFrom);
          const customEnd = new Date(customTo);
          customStart.setHours(0, 0, 0, 0);
          customEnd.setHours(23, 59, 59, 999);
          
          // Format without timezone offset
          return {
            fromDate: new Date(customStart.getTime() - (customStart.getTimezoneOffset() * 60000))
              .toISOString().split('.')[0],
            toDate: new Date(customEnd.getTime() - (customEnd.getTimezoneOffset() * 60000))
              .toISOString().split('.')[0]
          };
        }
        return timeRange;
    }
    
    // Format without timezone offset
    return {
      fromDate: new Date(start.getTime() - (start.getTimezoneOffset() * 60000))
        .toISOString().split('.')[0],
      toDate: new Date(end.getTime() - (end.getTimezoneOffset() * 60000))
        .toISOString().split('.')[0]
    };
  }, [timeRange]);

  const fetchHealthMetrics = useCallback(async (dateRange = timeRange) => {
    if (!userInfo?.user_id) return;
    setLoadingMetrics(true);
    try {
      const response = await axios.get(
        `${config.baseURL}/api/v1/users/${userInfo.user_id}/metrics`,
        {
          headers: { Authorization: `Bearer ${userInfo.token}` },
          params: { from: dateRange.fromDate, to: dateRange.toDate }
        }
      );
      setHealthMetrics(response.data);
    } catch (error) {
      console.error('Error fetching health metrics:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to load health data',
        text: 'Could not fetch health metrics from server',
        confirmButtonColor: '#d33',
      });
    } finally {
      setLoadingMetrics(false);
    }
  }, [userInfo, timeRange]);

  const handleTimeRangeChange = useCallback((type) => {
    setTimeRangeType(type);
    const range = calculateDateRange(type);
    setTimeRange(range);
    fetchHealthMetrics(range);
  }, [calculateDateRange, fetchHealthMetrics]);

  const handleCustomDateApply = useCallback((customFrom, customTo) => {
    if (customFrom && customTo) {
      const fromDate = new Date(customFrom);
      const toDate = new Date(customTo);
      
      if (fromDate > toDate) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Date Range',
          text: 'Start date must be before end date',
          confirmButtonColor: '#d33',
        });
        return false;
      }
      
      setTimeRangeType('custom');
      const range = calculateDateRange('custom', customFrom, customTo);
      setTimeRange(range);
      fetchHealthMetrics(range);
      return true;
    }
    return false;
  }, [calculateDateRange, fetchHealthMetrics]);

  useEffect(() => {
    const range = calculateDateRange('today');
    setTimeRange(range);
    if (userInfo?.user_id) {
      fetchHealthMetrics(range);
    }
  }, []);

  return {
    loadingMetrics,
    healthMetrics,
    timeRange,
    timeRangeType,
    setTimeRangeType,
    fetchHealthMetrics,
    handleTimeRangeChange,
    handleCustomDateApply,
    calculateDateRange
  };
};